/**
 * widget <-> 宿主 postMessage 桥。
 *
 * 约束来源：CONTRACT.md 第四节 iframe 接入要求 + SDK protocol.ts 定义的帧结构。
 *
 * 严格性口径（每条都有对应测试）：
 * 1. 默认精确匹配宿主来源；显式 '*' 接受 HTTP/HTTPS 父窗口，握手后锁定来源。
 *    入站始终校验 event.source；同源其它窗口不能冒充。
 * 2. 通配模式只有无身份信息的首次 widget.ready 使用 '*'，其余消息只发送到绑定来源。
 *    精确模式未绑定时仍向配置的每个精确来源发送。
 * 3. 帧结构必须经 SDK 的 parseHostToWidget 校验；非法帧只记录并忽略，不产生副作用。
 * 4. 本桥只做协议与守卫，不做业务：身份/HTTP 由 WidgetSession 负责。
 */

import type {
  HostContextPayload,
  WidgetCapturePayload,
  HostHandshakePayload,
  MessageEventLike,
  SupportPrincipalRef,
  WidgetStatusPayload,
} from '@support/sdk';
import {
  createWidgetMessage,
  HOST_CAPABILITY_SCREEN_CAPTURE,
  parsePreviewAckPayload,
  parseHandshakePayload,
  parseHostToWidget,
  parseOpenPayload,
  parseSessionPayload,
  parseContextPayload,
  SUPPORT_WIDGET_PROTOCOL_VERSION,
  SUPPORT_WIDGET_SUPPORTED_VERSIONS,
} from '@support/sdk';
import { newRequestId, normalizeOrigin, OriginAllowlist, TypedEmitter } from '@support/platform';
import type { Unsubscribe } from '@support/platform';
import { parseHostOrigins } from './frame-ancestors';

/** iframe 自身的 window（只用到 message 监听）。 */
export interface WidgetSelfWindow {
  addEventListener: (type: 'message', handler: (event: MessageEventLike) => void) => void;
  removeEventListener: (
    type: 'message',
    handler: (event: MessageEventLike) => void,
  ) => void;
}

/** 出站目标窗口；只有通配模式的首次 ready 通知可使用 '*'。 */
export interface WidgetPostWindow {
  postMessage: (message: unknown, targetOrigin: string) => void;
}

export type WidgetBridgeState = 'closed' | 'idle' | 'open' | 'stopped';

export type BridgeRejectedReason =
  | 'no-host'
  | 'origin'
  | 'schema'
  | 'source';

/** 握手结果：只能是"验证通过"或一个明确原因，绝不返回模糊成功。 */
export type HandshakeOutcome =
  /** principal 为 null：已收下源令牌待命，客户打开窗口时才换票建立连接。 */
  | { ok: true; principal: null | SupportPrincipalRef }
  | {
      message?: string;
      ok: false;
      reason: 'internal' | 'protocol-version' | 'schema' | 'superseded' | 'unauthorized';
    };

/**
 * 会话层需要提供给桥的能力（WidgetSession 结构化实现）。
 * 桥不直接持有 session 对象，避免协议层与业务层互相穿透。
 */
export interface WidgetBridgeHandlers {
  applyHostContext: (payload: HostContextPayload) => void;
  authenticate: (sourceToken: string) => Promise<HandshakeOutcome>;
  logout: () => Promise<void> | void;
  readonly status: WidgetStatusPayload;
  setOpen: (open: boolean) => void;
}

export interface WidgetBridgeEvents extends Record<string, unknown> {
  /** 宿主在握手时声明的能力（每次握手都会更新）。 */
  capabilities: { capabilities: readonly string[] };
  handshake: {
    accepted: boolean;
    principal: null | SupportPrincipalRef;
    reason?: string;
  };
  open: { open: boolean };
  rejected: { detail?: string; reason: BridgeRejectedReason };
}

export interface WidgetBridgeOptions {
  handlers: WidgetBridgeHandlers;
  /** 宿主精确来源列表，或单独的 '*'；空列表直接构造失败。 */
  hostOrigins: Iterable<string>;
  /** iframe 的父窗口（浏览器里的 window.parent）；用于出站与 source 校验。 */
  parentWindow?: null | WidgetPostWindow;
  protocolVersion?: string;
  selfWindow: WidgetSelfWindow;
  widgetVersion?: string;
}

export class WidgetBridge {
  private readonly allowAnyHost: boolean;

  private readonly allowlist: OriginAllowlist;

  private readonly emitter = new TypedEmitter<WidgetBridgeEvents>();

  private readonly handlers: WidgetBridgeHandlers;

  private readonly onMessageBound = (event: MessageEventLike): void => {
    void this.handleMessage(event);
  };

  private readonly options: WidgetBridgeOptions;

  private readonly protocolVersion: string;

  private capabilitiesValue: readonly string[] = [];

  private hostOriginValue: null | string = null;

  private hostSource: null | WidgetPostWindow;

  private listenersAttached = false;

  private stateValue: WidgetBridgeState = 'idle';

  private unsubscribes: Unsubscribe[] = [];

  constructor(options: WidgetBridgeOptions) {
    this.options = options;
    this.handlers = options.handlers;
    const hostOrigins = parseHostOrigins([...options.hostOrigins].join(','));
    this.allowAnyHost = hostOrigins.length === 1 && hostOrigins[0] === '*';
    this.allowlist = new OriginAllowlist(hostOrigins);
    if (!this.allowAnyHost && this.allowlist.size === 0) {
      throw new Error('宿主来源白名单不能为空：缺少白名单时 widget 无法安全地回复宿主');
    }
    this.protocolVersion = options.protocolVersion ?? SUPPORT_WIDGET_PROTOCOL_VERSION;
    this.hostSource = options.parentWindow ?? null;
  }

  /** 宿主能否配合截图（隐藏、铺满、恢复客服窗口）；旧宿主不声明时为 false。 */
  get canCaptureScreen(): boolean {
    return this.capabilitiesValue.includes(HOST_CAPABILITY_SCREEN_CAPTURE);
  }

  /** 请宿主切换客服窗口显示；宿主没声明截图能力时不发送，返回 false。 */
  requestCapture(phase: WidgetCapturePayload['phase']): boolean {
    if (!this.canCaptureScreen) return false;
    return this.postToHost(createWidgetMessage('widget.capture', { phase }));
  }

  private readonly previewWaiters = new Map<string, { resolve: (shown: boolean) => void; timer: ReturnType<typeof setTimeout> }>();

  /**
   * 请宿主在浏览器页面上浮出这张图（不进入浏览器全屏）。
   * 宿主确认后返回 true；旧宿主不回应时返回 false，调用方改在窗内预览。
   */
  requestImagePreview(file: File): Promise<boolean> {
    if (!file.type.startsWith('image/') || file.size <= 0 || file.size > 10 * 1024 * 1024) {
      return Promise.resolve(false);
    }
    const requestId = newRequestId();
    const sent = this.postToHost(createWidgetMessage('widget.preview', {
      image: file,
      mime: file.type,
      name: file.name.slice(0, 200),
      phase: 'open',
    }, requestId));
    if (!sent) return Promise.resolve(false);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.previewWaiters.delete(requestId);
        resolve(false);
      }, 300);
      this.previewWaiters.set(requestId, {
        timer,
        resolve: (shown) => {
          clearTimeout(timer);
          this.previewWaiters.delete(requestId);
          resolve(shown);
        },
      });
    });
  }

  /** 关掉外层页面上的大图。 */
  closeImagePreview(): void {
    this.postToHost(createWidgetMessage('widget.preview', { phase: 'close' }));
  }

  /** 已绑定的宿主精确 origin（未收到过合法帧时为 null）。 */
  get hostOrigin(): null | string {
    return this.hostOriginValue;
  }

  get state(): WidgetBridgeState {
    return this.stateValue;
  }

  on<K extends keyof WidgetBridgeEvents>(
    type: K,
    handler: (payload: WidgetBridgeEvents[K]) => void,
  ): Unsubscribe {
    return this.emitter.on(type, handler);
  }

  /** 开始监听宿主 postMessage，并上报本端 widget.ready。 */
  start(): void {
    if (this.listenersAttached) return;
    this.options.selfWindow.addEventListener('message', this.onMessageBound);
    this.listenersAttached = true;
    this.stateValue = 'closed';
    const ready = createWidgetMessage('widget.ready', {
      protocolVersion: this.protocolVersion,
      supportedProtocolVersions: [...SUPPORT_WIDGET_SUPPORTED_VERSIONS],
      ...(this.options.widgetVersion === undefined
        ? {}
        : { widgetVersion: this.options.widgetVersion }),
    });
    if (this.allowAnyHost && this.hostOriginValue === null) {
      // SDK 收到 ready 才发起握手。此通知只含公开版本信息，不含身份或会话状态。
      this.options.parentWindow?.postMessage(ready, '*');
    } else {
      this.postToHost(ready);
    }
  }

  /** 停止监听并释放会话层订阅（页面卸载 / 宿主销毁 iframe 时调用）。 */
  stop(): void {
    if (this.listenersAttached) {
      this.options.selfWindow.removeEventListener('message', this.onMessageBound);
      this.listenersAttached = false;
    }
    for (const unsubscribe of this.unsubscribes) unsubscribe();
    this.unsubscribes = [];
    for (const waiter of [...this.previewWaiters.values()]) waiter.resolve(false);
    this.previewWaiters.clear();
    this.stateValue = 'stopped';
  }

  /** 广播 widget 状态（不是某个在飞请求的应答，因此不带 requestId）。 */
  publishStatus(payload?: WidgetStatusPayload): void {
    this.postToHost(
      createWidgetMessage('widget.status', { ...(payload ?? this.handlers.status) }),
    );
  }

  /** 把会话层的业务事件转给宿主。 */
  publishEvent(payload: Record<string, unknown>): void {
    this.postToHost(createWidgetMessage('widget.event', payload));
  }

  private async handleMessage(event: MessageEventLike): Promise<void> {
    const origin = normalizeOrigin(event.origin);
    if (origin === null || (!this.allowAnyHost && !this.allowlist.has(origin)) ||
        (this.allowAnyHost && this.hostOriginValue !== null && origin !== this.hostOriginValue)) {
      this.reject('origin', `origin 不符合宿主来源策略: ${event.origin}`);
      return;
    }
    if (!this.isTrustedSource(event.source)) {
      this.reject('source', 'event.source 不是受信任的宿主窗口');
      return;
    }
    const message = parseHostToWidget(event.data);
    if (!message) {
      this.reject('schema', '帧结构不符合协议（ns/v/type/payload/requestId）');
      return;
    }
    if (this.allowAnyHost && this.hostOriginValue === null && message.type !== 'host.handshake') {
      this.reject('schema', '通配模式必须先由父窗口发起握手');
      return;
    }
    // 首个合法帧绑定精确 origin：之后出站只投递这一个 origin
    this.hostOriginValue ??= origin;
    switch (message.type) {
      case 'host.handshake': {
        await this.handleHandshake(message.requestId, message.payload);
        return;
      }
      case 'host.session': {
        await this.handleSession(message.payload);
        return;
      }
      case 'host.logout': {
        await this.handlers.logout();
        this.publishStatus();
        return;
      }
      case 'host.context': {
        const payload = parseContextPayload(message.payload);
        if (!payload) {
          this.reject('schema', 'host.context 载荷非法');
          return;
        }
        this.handlers.applyHostContext(payload);
        return;
      }
      case 'host.open': {
        const payload = parseOpenPayload(message.payload);
        if (!payload) {
          this.reject('schema', 'host.open 载荷非法');
          return;
        }
        this.stateValue = payload.open ? 'open' : 'closed';
        this.handlers.setOpen(payload.open);
        this.emitter.emit('open', { open: payload.open });
        return;
      }
      case 'host.preview': {
        const payload = parsePreviewAckPayload(message.payload);
        const requestId = message.requestId;
        const done = requestId ? this.previewWaiters.get(requestId) : undefined;
        if (!payload || !done) return;
        done.resolve(true);
        return;
      }
      default: {
        this.reject('schema', `未处理的帧类型: ${String(message.type)}`);
      }
    }
  }

  private async handleHandshake(
    requestId: undefined | string,
    payload: null | Record<string, unknown> | undefined,
  ): Promise<void> {
    const handshake = parseHandshakePayload(payload);
    if (!handshake) {
      this.postHandshakeError(requestId, 'schema', 'host.handshake 载荷非法');
      this.reject('schema', 'host.handshake 载荷非法');
      return;
    }
    this.capabilitiesValue = [...(handshake.capabilities ?? [])];
    this.emitter.emit('capabilities', { capabilities: this.capabilitiesValue });
    if (handshake.protocolVersion !== this.protocolVersion) {
      // 不静默降级：版本谈不拢就明确失败
      this.postHandshakeError(
        requestId,
        'protocol-version',
        `widget 不支持协议版本 ${handshake.protocolVersion}`,
      );
      this.emitter.emit('handshake', {
        accepted: false,
        principal: null,
        reason: 'protocol-version',
      });
      return;
    }
    if (handshake.sourceToken === null) {
      this.postHandshakeError(requestId, 'unauthorized', '缺少源令牌');
      this.emitter.emit('handshake', {
        accepted: false,
        principal: null,
        reason: 'unauthorized',
      });
      return;
    }
    const outcome = await this.handlers.authenticate(handshake.sourceToken);
    if (!outcome.ok) {
      const reason = outcome.reason === 'superseded' ? 'internal' : outcome.reason;
      this.postHandshakeError(requestId, reason, outcome.message);
      this.emitter.emit('handshake', {
        accepted: false,
        principal: null,
        reason: outcome.reason,
      });
      return;
    }
    this.postToHost(
      createWidgetMessage(
        'widget.handshake.ack',
        {
          accepted: true,
          principal: outcome.principal,
          protocolVersion: this.protocolVersion,
        },
        requestId,
      ),
    );
    // 身份已就绪：此刻才同步宿主上报的页面上下文，避免旧身份上下文污染新身份
    this.applyHandshakeContext(handshake);
    this.emitter.emit('handshake', {
      accepted: true,
      principal: outcome.principal,
    });
    this.publishStatus();
  }

  private async handleSession(
    payload: null | Record<string, unknown> | undefined,
  ): Promise<void> {
    const session = parseSessionPayload(payload);
    if (!session) {
      this.reject('schema', 'host.session 载荷非法');
      return;
    }
    const outcome = await this.handlers.authenticate(session.sourceToken);
    if (!outcome.ok && outcome.reason !== 'superseded') {
      this.reject('schema', `会话身份切换失败: ${outcome.reason}`);
    }
    this.publishStatus();
  }

  private applyHandshakeContext(handshake: HostHandshakePayload): void {
    if (handshake.context === null) return;
    this.handlers.applyHostContext({
      context: handshake.context,
      pageInstanceId: handshake.pageInstanceId,
      version: handshake.context.version,
    });
  }

  private postHandshakeError(
    requestId: undefined | string,
    reason: 'internal' | 'protocol-version' | 'schema' | 'unauthorized',
    message?: string,
  ): void {
    this.postToHost(
      createWidgetMessage(
        'widget.handshake.error',
        { reason, ...(message === undefined ? {} : { message }) },
        requestId,
      ),
    );
  }

  private isTrustedSource(source: unknown): boolean {
    if (this.hostSource !== null) return source === this.hostSource;
    // 任意来源模式不能让直接打开的页面认领任意窗口为宿主。
    if (this.allowAnyHost) return false;
    // 未持有父窗口引用时（例如被直接打开），首个通过 origin 校验的窗口成为宿主
    if (!isPostWindow(source)) return false;
    this.hostSource = source;
    return true;
  }

  private target(): null | WidgetPostWindow {
    return this.hostSource ?? this.options.parentWindow ?? null;
  }

  private postToHost(message: unknown): boolean {
    const target = this.target();
    if (target === null) {
      this.reject('no-host', '缺少宿主窗口引用，无法出站');
      return false;
    }
    if (this.hostOriginValue !== null) {
      target.postMessage(message, this.hostOriginValue);
      return true;
    }
    // 未绑定前不广播状态、身份、错误或业务事件；ready 由 start 单独发送。
    if (this.allowAnyHost) return false;
    // 尚未绑定：按白名单里的每个精确 origin 各投一次，绝不使用 '*'
    for (const origin of this.allowlist.list()) {
      target.postMessage(message, origin);
    }
    return true;
  }

  private reject(reason: BridgeRejectedReason, detail?: string): void {
    this.emitter.emit('rejected', { ...(detail === undefined ? {} : { detail }), reason });
  }
}

function isPostWindow(value: unknown): value is WidgetPostWindow {
  if (typeof value !== 'object' || value === null) return false;
  return typeof (value as { postMessage?: unknown }).postMessage === 'function';
}
